import { Add } from '@mui/icons-material';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { createDocumentsFromCompany } from '../../../../../../../../lib/api';
import CustomButton from '../../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions from '../../../../../../../../lib/designSystem/HeaderWithActions';
import EnhancedTable from '../../../../../../../../lib/designSystem/Table';
import {
  formatDateBack,
  formatMoney,
  formatMoneyBack,
  showAlert,
  validaRut,
} from '../../../../../../../../lib/functions/utils';
import { type AddDocumentsManuallyProps, type NewDocument } from '../../types';

const AddDocumentsManually = (props: AddDocumentsManuallyProps) => {
  const {
    open,
    setOpen,
    subProductSelected,
    business,
    setSuccessOpen,
    handleSearchSuggestedDocuments,
    onDocumentsCreated,
  } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState<NewDocument[]>([]);
  const [newDocument, setNewDocument] = useState<NewDocument>({
    folio: '',
    businessIdentifier: business.identifier,
    stakeholderIdentifier: '',
    stakeholderName: '',
    issuedDate: '',
    amount: 0,
  });

  const existingFolio = (folio: string) => {
    return documents.some((invoice: NewDocument) => invoice.folio === folio);
  };

  const validateRuts = () => {
    if (validaRut(newDocument.stakeholderIdentifier)) {
      return true;
    }
    return false;
  };

  const checkAllFields = () => {
    if (
      newDocument.folio === '' ||
      newDocument.businessIdentifier === '' ||
      newDocument.stakeholderIdentifier === '' ||
      newDocument.issuedDate === '' ||
      newDocument.amount === 0 ||
      newDocument.stakeholderName === ''
    ) {
      showAlert(dispatch, 'error', 'Debes llenar todos los campos');
      return false;
    }
    return true;
  };

  const handleChangeDate = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const [year, month, day] = e.target.value.split('-');
    setNewDocument({ ...newDocument, issuedDate: `${day}${month}${year}` });
  };

  const uploadDocuments = async () => {
    setLoading(true);
    try {
      const formatDocumentsWithDate = documents.map((invoice) => {
        return {
          ...invoice,
          issuedDate: new Date(invoice.issuedDate).toISOString(),
          amount: invoice.amount,
        };
      });
      const response = await createDocumentsFromCompany(dispatch, {
        documents: formatDocumentsWithDate,
        businessIdentifier: business.identifier,
        subProductId: subProductSelected,
      });

      // Extraer documentos creados y repetidos
      const createdDocuments = response?.documentCreated || [];
      const repeatedDocuments = response?.documentsRepetead || [];

      setSuccessOpen('Documentos agregados correctamente');
      setOpen(false);

      // Llamar al callback con los documentos completos o fallback al comportamiento anterior
      if (
        onDocumentsCreated &&
        (createdDocuments.length > 0 || repeatedDocuments.length > 0)
      ) {
        await onDocumentsCreated(
          { created: createdDocuments, repeated: repeatedDocuments },
          documents.length,
        );
      } else {
        await handleSearchSuggestedDocuments({
          page: 1,
          pageSize: 10,
          subProductOverride: subProductSelected,
        });
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleAddInvoice = async () => {
    setLoading(true);
    if (existingFolio(newDocument.folio)) {
      alert('Ya existe una factura con este folio');
      return;
    }
    if (!checkAllFields()) return;
    if (!validateRuts()) {
      showAlert(dispatch, 'error', 'RUT inválido');
      return;
    }
    const formattedDocument: NewDocument = {
      ...newDocument,
      issuedDate: formatDateBack(newDocument.issuedDate),
    };
    setDocuments((prev: NewDocument[]) => [...prev, formattedDocument]);
    setNewDocument({
      folio: '',
      businessIdentifier: business.identifier,
      stakeholderIdentifier: '',
      stakeholderName: '',
      issuedDate: '',
      amount: 0,
    });
    setLoading(false);
  };

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <Dialog maxWidth="md" open={open} onClose={() => setOpen(false)}>
      <DialogTitle>
        <HeaderWithActions title="Agregar documentos" type="dialogHeader" />
      </DialogTitle>
      {loading ? (
        <CenteredCircularProgress type="dialog" />
      ) : (
        <DialogContent>
          <Typography variant="body1">
            A continuación, ingresa los datos de los documentos que quieres
            ingresar.
          </Typography>
          <div style={{ padding: '20px 0px' }}>
            <Grid container spacing={2} alignItems={'center'}>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  label="Folio"
                  value={newDocument.folio}
                  onChange={(e) =>
                    setNewDocument({ ...newDocument, folio: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  label="RUT receptor *"
                  value={newDocument.stakeholderIdentifier}
                  onChange={(e) =>
                    setNewDocument({
                      ...newDocument,
                      stakeholderIdentifier: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={newDocument.stakeholderName}
                  onChange={(e) =>
                    setNewDocument({
                      ...newDocument,
                      stakeholderName: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Fecha de Emisión"
                  type="date"
                  value={formatDateBack(newDocument.issuedDate)}
                  onChange={(e) => handleChangeDate(e)}
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  label="Monto"
                  value={formatMoney(newDocument.amount)}
                  onChange={(e) =>
                    setNewDocument({
                      ...newDocument,
                      amount: formatMoneyBack(e.target.value) || 0,
                    })
                  }
                />
              </Grid>

              <Grid item xs={1}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <IconButton
                    color="primary"
                    onClick={() => {
                      void handleAddInvoice();
                    }}
                  >
                    <Add />
                  </IconButton>
                )}
              </Grid>
            </Grid>
            <Typography variant="caption">
              * Rut sin puntos y con guión
            </Typography>
          </div>
          {documents.length > 0 && (
            <div>
              <Typography variant="body1">Documentos:</Typography>
              <div>
                <EnhancedTable
                  dataHeaders={[
                    { key: 'folio', label: 'Folio' },
                    { key: 'stakeholderIdentifier', label: 'Rut receptor' },
                    { key: 'stakeholderName', label: 'Nombre receptor' },
                    {
                      key: 'issuedDate',
                      label: 'Fecha emisión',
                      type: 'eight-days-date',
                    },
                    { key: 'amount', label: 'Monto', type: 'money' },
                  ]}
                  data={documents}
                  finalSumRow
                />
              </div>
            </div>
          )}
        </DialogContent>
      )}
      <DialogActions>
        <CustomButton
          variant="contained"
          color="secondary"
          onClick={() => setOpen(false)}
        >
          {' '}
          Salir
        </CustomButton>
        <CustomButton
          variant="contained"
          color="primary"
          disabled={!documents.length}
          onClick={() => {
            void uploadDocuments();
          }}
        >
          {' '}
          Subir
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddDocumentsManually;
